// ui/src/pages/Framework.tsx
import { Link } from 'react-router-dom';
import { PublicLayout } from '../components/layout/PublicLayout';
import { SectionLabel } from '../components/common/SectionLabel';
import { Card } from '../components/common/Card';
import { Button } from '../components/common/Button';
import { usePublicFramework } from '../hooks/useFramework';

export function FrameworkPage() {
  const { data: framework, isLoading, error } = usePublicFramework();

  if (isLoading) {
    return (
      <PublicLayout>
        <div className="py-16 px-6">
          <div className="max-w-6xl mx-auto text-center">
            <p className="text-wolf-300">Loading framework...</p>
          </div>
        </div>
      </PublicLayout>
    );
  }

  if (error || !framework) {
    return (
      <PublicLayout>
        <div className="py-16 px-6">
          <div className="max-w-6xl mx-auto text-center">
            <p className="text-red-400">Failed to load framework configuration.</p>
          </div>
        </div>
      </PublicLayout>
    );
  }

  const thresholdDisplay = [
    {
      range: `${framework.thresholds.GREEN}-100`,
      label: 'GREEN LIGHT',
      color: 'text-action border-action bg-action/20',
    },
    {
      range: `${framework.thresholds.CONDITIONAL}-${framework.thresholds.GREEN - 1}`,
      label: 'CONDITIONAL',
      color: 'text-amber-500 border-amber-500 bg-amber-500/20',
    },
    {
      range: `${framework.thresholds.HOLD}-${framework.thresholds.CONDITIONAL - 1}`,
      label: 'HOLD',
      color: 'text-wolf-500 border-wolf-500 bg-wolf-500/20',
    },
    {
      range: `<${framework.thresholds.HOLD}`,
      label: 'PASS',
      color: 'text-red-500 border-red-500 bg-red-500/20',
    },
  ];

  return (
    <PublicLayout>
      <div className="py-16 px-6">
        <div className="max-w-6xl mx-auto">
          {/* Hero */}
          <section className="mb-16">
            <SectionLabel>Evaluation Framework</SectionLabel>
            <h1 className="text-4xl font-bold text-white mb-4">{framework.name}</h1>
            <p className="text-xl text-wolf-300 max-w-3xl">{framework.description}</p>
          </section>

          {/* Framework Cards */}
          <section className="mb-16">
            <SectionLabel>Framework Components</SectionLabel>
            <h2 className="text-2xl font-bold text-white mb-8">Weighted Evaluation Criteria</h2>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {framework.sections.map((section) => (
                <Card key={section.id} variant="metric">
                  <div className="flex items-baseline justify-between mb-4">
                    <h3 className="text-lg font-semibold text-white">{section.name}</h3>
                    <span className="font-mono text-xl text-action">
                      {Math.round(section.weight * 100)}%
                    </span>
                  </div>
                  <p className="text-wolf-300 mb-4">
                    {section.criteria.length} criteria evaluating{' '}
                    {section.criteria.map((c) => c.name.toLowerCase()).join(', ')}.
                  </p>
                  <p className="text-xs text-wolf-500 font-mono">{section.framework}</p>
                </Card>
              ))}
            </div>
          </section>

          {/* Decision Thresholds */}
          <section className="mb-16">
            <SectionLabel>Decision Thresholds</SectionLabel>
            <h2 className="text-2xl font-bold text-white mb-8">Score Interpretation</h2>

            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {thresholdDisplay.map((threshold) => (
                <div
                  key={threshold.label}
                  className={`p-4 border font-mono text-center ${threshold.color}`}
                >
                  <p className="text-2xl font-bold mb-1">{threshold.range}</p>
                  <p className="text-xs uppercase tracking-wide">{threshold.label}</p>
                </div>
              ))}
            </div>
          </section>

          {/* CTA */}
          <section className="text-center py-12 bg-wolf-900/30 border border-wolf-700/50">
            <h2 className="text-2xl font-bold text-white mb-4">
              Ready to Submit Your Proposal?
            </h2>
            <p className="text-wolf-300 mb-8 max-w-xl mx-auto">
              Get your R&D initiative evaluated against our comprehensive framework.
            </p>
            <Link to="/">
              <Button>Get Started</Button>
            </Link>
          </section>
        </div>
      </div>

      <footer className="py-12 px-6 border-t border-wolf-700/50">
        <div className="max-w-6xl mx-auto text-center">
          <p className="text-wolf-500 text-sm">
            &copy; {new Date().getFullYear()} Catalyst. All rights reserved.
          </p>
        </div>
      </footer>
    </PublicLayout>
  );
}
